import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Hammer, RefreshCw } from "lucide-react";

import { PageHeader, btn, Loader } from "@/components/Page";
import { useMiningDashboard } from "@/hooks/useMiningDashboard";
import { MiningTabs } from "@/components/mining/MiningTabs";
import { MiningSummaryPanel } from "@/components/mining/MiningSummaryPanel";
import { MiningResourcesPanel } from "@/components/mining/MiningResourcesPanel";
import { MiningSmithingRecipesPanel } from "@/components/mining/MiningSmithingRecipesPanel";
import { MiningToolUpgradePanel } from "@/components/mining/MiningToolUpgradePanel";
import { MiningProjectsPanel } from "@/components/mining/MiningProjectsPanel";
import { MiningConfirmDialog } from "@/components/mining/MiningConfirmDialog";
import { MiningEquipmentLink } from "@/components/mining/MiningEquipmentLink";

export default function MiningSmithing() {
  const [confirm, setConfirm] = useState(null);
  const {
    data,
    loading,
    loadMining,
    smithingBusyKey,
    toolUpgradeBusyKey,
    boostBusyKey,
    projectBusyKey,
    projectClaimBusy,
    liveSmithing,
    handleCraftSmithingMaterial,
    handleUpgradeTool,
    handleActivateBoost,
    handleStartProject,
    handleClaimProject,
  } = useMiningDashboard();

  const confirmBusy = (confirm?.type === "craft" && smithingBusyKey === confirm?.materialId)
    || (confirm?.type === "upgrade" && toolUpgradeBusyKey === confirm?.toolId)
    || (confirm?.type === "boost" && boostBusyKey === confirm?.materialId)
    || (confirm?.type === "project" && projectBusyKey === confirm?.projectId)
    || (confirm?.type === "project-claim" && projectClaimBusy);

  const handleBonus = (data?.smithing?.materials || []).find((material) => material.id === "reinforced_handle")?.tool_upgrade?.max_durability_bonus || 0;
  const smithingData = liveSmithing || data?.smithing;
  const smithingJumpLinks = [
    { targetId: "smithing-materials-section", testId: "smithing-materials", label: "Smithing Materials" },
    { targetId: "pickaxe-upgrades-section", testId: "pickaxe-upgrades", label: "Pickaxe Upgrades" },
  ];

  if (loading && !data) return <Loader label="Loading Smithing..." />;

  return (
    <div data-testid="mining-smithing-page" className="space-y-6">
      <PageHeader
        title="Smithing"
        subtitle="Turn discoveries and raw ore into long-term mining upgrades and forge enhancers."
        action={
          <button data-testid="mining-smithing-refresh-button" type="button" onClick={() => loadMining()} className={btn("ghost") + " flex items-center gap-2"}>
            <RefreshCw size={15} /> Refresh
          </button>
        }
      />

      <MiningEquipmentLink />

      <MiningTabs claimableCount={data?.discoveries?.claimable_count || 0} />

      <div className="grid xl:grid-cols-[1fr_1fr] gap-6">
        <MiningSummaryPanel profile={data?.profile} smithing={smithingData} jumpLinks={smithingJumpLinks} />
        <MiningResourcesPanel profile={data?.profile} showHistory={false} />
      </div>

      <MiningProjectsPanel
        smithing={smithingData}
        projectBusyKey={projectBusyKey}
        projectClaimBusy={projectClaimBusy}
        onStartProject={(project) => setConfirm({
          type: "project",
          projectId: project.id,
          title: `Start ${project.name}`,
          message: `Start ${project.name}?

This will invest ${Object.entries(project.ore_costs || {}).map(([rarity, amount]) => `${amount} ${rarity} ore`).join(", ")} and take ${project.duration_label}.`,
          confirmLabel: "Start Project",
          onConfirm: async () => {
            await handleStartProject(project.id);
            setConfirm(null);
          },
        })}
        onClaimProject={() => setConfirm({
          type: "project-claim",
          title: "Claim Completed Project",
          message: `Claim this completed mining project and make its bonus permanent?`,
          confirmLabel: "Claim Project",
          onConfirm: async () => {
            await handleClaimProject();
            setConfirm(null);
          },
        })}
      />

      <MiningSmithingRecipesPanel
        sectionId="smithing-materials-section"
        smithing={smithingData}
        smithingBusyKey={smithingBusyKey}
        boostBusyKey={boostBusyKey}
        onCraft={(material) => setConfirm({
          type: "craft",
          materialId: material.id,
          title: `Craft ${material.name}`,
          message: `Craft 1 ${material.name}?\n\nThis will spend ${Object.entries(material.ore_costs || {}).map(([rarity, amount]) => `${amount} ${rarity} ore`).join(", ")}.`,
          confirmLabel: "Craft Material",
          onConfirm: async () => {
            await handleCraftSmithingMaterial(material.id, 1);
            setConfirm(null);
          },
        })}
        onActivateBoost={(material) => setConfirm({
          type: "boost",
          materialId: material.id,
          title: `Prime ${material.name}`,
          message: `${material.name} will be consumed and apply its bonus to your next mining session only.`,
          confirmLabel: "Prime Next Session",
          onConfirm: async () => {
            await handleActivateBoost(material.id);
            setConfirm(null);
          },
        })}
      />

      <MiningToolUpgradePanel
        sectionId="pickaxe-upgrades-section"
        tools={data?.tools || []}
        smithing={data?.smithing}
        toolUpgradeBusyKey={toolUpgradeBusyKey}
        onUpgrade={(tool) => setConfirm({
          type: "upgrade",
          toolId: tool.id,
          title: `Upgrade ${tool.name}`,
          message: `Apply a Reinforced Handle to ${tool.name}?\n\nThis permanently adds +${handleBonus} max durability to that pickaxe.`,
          confirmLabel: "Apply Upgrade",
          onConfirm: async () => {
            await handleUpgradeTool(tool.id, "reinforced_handle");
            setConfirm(null);
          },
        })}
      />


      <MiningConfirmDialog confirm={confirm} busy={confirmBusy} onClose={() => setConfirm(null)} />
    </div>
  );
}